import React, { useState, useEffect } from 'react';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { Sidebar, MobileHeader } from '../components/shared/Sidebar';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import axios from '../lib/axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, UserPlus, TrendingUp, TrendingDown, Users, Crown, Flame, 
  Trophy, AlertTriangle, Edit2, Trash2, X, DollarSign, ArrowUpRight, 
  ArrowDownRight, Clock, Sparkles, Zap, Award, Star, RefreshCw, Check,
  Sword, Shield, Gem, Target, Skull, Heart, Crosshair, Swords, Plus, BookOpen
} from 'lucide-react';
import { formatCurrency, formatDateTime, formatRelativeTime } from '../lib/utils';
import { usePageFilter } from '../contexts/PageFilterContext';
import { PageFilterTabs } from '../components/shared/PageFilterTabs';

// Particle Background
function ParticleBackground() {
  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden">
      {[...Array(20)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-1 h-1 rounded-full"
          style={{
            left: `${Math.random() * 100}%`,
            background: ['#00F5FF', '#9945FF', '#FFD700'][i % 3]
          }}
          animate={{
            y: [Math.random() * 100 + '%', '-10%'],
            opacity: [0, 0.5, 0]
          }}
          transition={{
            duration: 10 + Math.random() * 10,
            repeat: Infinity,
            delay: Math.random() * 5
          }}
        />
      ))}
    </div>
  );
}

// Gaming Avatar Component
function GamingAvatar({ name, rank = 'common', size = 'md' }) {
  const sizes = { sm: 'w-10 h-10', md: 'w-12 h-12', lg: 'w-16 h-16' };
  const rankColors = {
    legendary: 'from-[#FFD700] to-[#FF6B00]',
    epic: 'from-[#9945FF] to-[#FF00E5]',
    rare: 'from-[#00F5FF] to-[#0088FF]',
    common: 'from-gray-500 to-gray-600'
  };
  
  return (
    <div className={`relative ${sizes[size]} rounded-xl p-[2px] bg-gradient-to-br ${rankColors[rank]}`}>
      <Avatar className={`${sizes[size]} rounded-xl border-2 border-black`}>
        <AvatarFallback className="bg-[#1a1a2e] text-white font-bold rounded-xl">
          {name?.slice(0, 2).toUpperCase()}
        </AvatarFallback>
      </Avatar>
    </div>
  );
}

// Animated Counter
function AnimatedNumber({ value, prefix = '' }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const end = parseFloat(value) || 0;
    const duration = 1500;
    const start = Date.now();
    const animate = () => {
      const elapsed = Date.now() - start;
      const progress = Math.min(elapsed / duration, 1);
      setDisplay(end * progress);
      if (progress < 1) requestAnimationFrame(animate);
    };
    animate();
  }, [value]);
  return <span className="font-mono">{prefix}{Math.floor(display).toLocaleString()}</span>;
}

const TIME_PERIODS = [
  { id: '1d', label: '24H', days: 1 },
  { id: '7d', label: '7D', days: 7 },
  { id: '30d', label: '30D', days: 30 },
  { id: '90d', label: '3M', days: 90 },
];

export default function PlayersPage() {
  const { selectedPageId, branchPages, dateRange } = usePageFilter();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [players, setPlayers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [timePeriod, setTimePeriod] = useState('7d');
  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const [showEditPlayer, setShowEditPlayer] = useState(null);
  const [showPlayerDetail, setShowPlayerDetail] = useState(null);
  const [playerForm, setPlayerForm] = useState({ name: '', page_id: '' });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 20;

  useEffect(() => {
    fetchPlayers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, currentPage, selectedPageId, dateRange]);

  const fetchPlayers = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (selectedPageId) params.append('page_id', selectedPageId);
      if (dateRange.startDate) params.append('start_date', dateRange.startDate);
      if (dateRange.endDate) params.append('end_date', dateRange.endDate);
      params.append('page', currentPage);
      params.append('limit', pageSize);
      
      const response = await axios.get(`/players?${params.toString()}`);
      
      // Handle both paginated and non-paginated response
      if (response.data.players) {
        setPlayers(response.data.players);
        setTotalCount(response.data.total || response.data.players.length);
        setTotalPages(Math.ceil((response.data.total || response.data.players.length) / pageSize));
      } else if (Array.isArray(response.data)) {
        setPlayers(response.data);
        setTotalCount(response.data.length);
        setTotalPages(Math.ceil(response.data.length / pageSize));
      }
    } catch (error) {
      toast.error('Failed to load warriors');
    } finally {
      setLoading(false);
    }
  };

  useAutoRefresh(fetchPlayers, 15000);

  const handleAddPlayer = async (e) => {
    e.preventDefault();
    try {
      const payload = { name: playerForm.name };
      if (playerForm.page_id) payload.page_id = playerForm.page_id;
      await axios.post('/players', payload);
      toast.success('New Warrior Recruited!');
      setShowAddPlayer(false);
      setPlayerForm({ name: '', page_id: '' });
      fetchPlayers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    }
  };

  const handleEditPlayer = async (e) => {
    e.preventDefault();
    try {
      const payload = { name: playerForm.name };
      if (playerForm.page_id !== undefined) payload.page_id = playerForm.page_id || null;
      await axios.put(`/players/${showEditPlayer.id}`, payload);
      toast.success('Warrior updated!');
      setShowEditPlayer(null);
      setPlayerForm({ name: '', page_id: '' });
      fetchPlayers();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const handleDeletePlayer = async (playerId) => {
    setDeleteConfirm(playerId);
  };

  const confirmDeletePlayer = async () => {
    if (!deleteConfirm) return;
    try {
      await axios.delete(`/players/${deleteConfirm}`);
      toast.success('Warrior Banished!');
      fetchPlayers();
    } catch (error) {
      toast.error('Failed');
    } finally {
      setDeleteConfirm(null);
    }
  };

  const getPlayerRank = (player) => {
    if (player.total_deposits >= 5000) return 'legendary';
    if (player.total_deposits >= 2000) return 'epic';
    if (player.total_deposits >= 500) return 'rare';
    return 'common';
  };

  const getTopWinners = () => [...players].sort((a, b) => b.holding_amount - a.holding_amount).slice(0, 5);
  const getTopDepositors = () => [...players].sort((a, b) => b.total_deposits - a.total_deposits).slice(0, 5);

  const getPageName = (pageId) => {
    const pg = branchPages.find(p => p.id === pageId);
    return pg ? pg.name : 'Unassigned';
  };

  // Filter players by time period for stats
  const getFilteredPlayersForStats = () => {
    const now = new Date();
    const period = TIME_PERIODS.find(p => p.id === timePeriod);
    if (!period) return players;
    const cutoff = new Date(now.getTime() - period.days * 24 * 60 * 60 * 1000);
    return players.filter(p => {
      const lastAct = new Date(p.last_activity);
      return lastAct >= cutoff;
    });
  };

  const statsPlayers = getFilteredPlayersForStats();
  const totalDeposits = statsPlayers.reduce((sum, p) => sum + (p.total_deposits || 0), 0);
  const totalWithdrawals = statsPlayers.reduce((sum, p) => sum + (p.total_withdrawals || 0), 0);
  const totalHolding = statsPlayers.reduce((sum, p) => sum + (p.holding_amount || 0), 0);

  return (
    <div className="min-h-screen bg-[#080810]" data-testid="players-page">
      <ParticleBackground />
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <MobileHeader onMenuClick={() => setSidebarOpen(true)} />

      {/* Background effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-[#00F5FF]/5 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-[#9945FF]/5 rounded-full blur-[150px]" />
      </div>

      <main className="lg:ml-72 min-h-screen relative z-10 p-4 md:p-6 lg:p-8">
        {/* Epic Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <motion.div
                animate={{ rotate: [0, 5, -5, 0] }}
                transition={{ repeat: Infinity, duration: 3 }}
                className="p-3 rounded-xl bg-gradient-to-br from-[#00F5FF]/20 to-[#9945FF]/20 border border-[#00F5FF]/30"
              >
                <Sword className="w-8 h-8 text-[#00F5FF]" />
              </motion.div>
              <div>
                <h1 className="text-3xl md:text-4xl font-black text-white">WARRIORS GUILD</h1>
                <p className="text-gray-400 flex items-center gap-2">
                  <Users className="w-4 h-4" /> {players.length} Active Warriors
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Time Period */}
              <div className="flex bg-[#0d0d15] rounded-xl p-1 border border-white/5">
                {TIME_PERIODS.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setTimePeriod(p.id)}
                    data-testid={`time-period-${p.id}`}
                    className={`px-3 py-1.5 rounded-lg text-sm font-bold transition-all ${
                      timePeriod === p.id
                        ? 'bg-gradient-to-r from-[#9945FF] to-[#00F5FF] text-black'
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowAddPlayer(true)}
                className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#00F5FF] to-[#9945FF] text-black font-bold flex items-center gap-2 relative z-20"
                data-testid="add-player-btn"
              >
                <UserPlus className="w-4 h-4" />
                RECRUIT
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* Stats Cards */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6"
        >
          {[
            { title: 'TOTAL WARRIORS', value: statsPlayers.length, icon: Sword, color: '#00F5FF' },
            { title: 'GOLD COLLECTED', value: totalDeposits, prefix: '$', icon: DollarSign, color: '#FFD700' },
            { title: 'GOLD PAID OUT', value: totalWithdrawals, prefix: '$', icon: TrendingDown, color: '#FF3366' },
            { title: 'HOLDING POWER', value: totalHolding, prefix: '$', icon: Shield, color: '#9945FF' },
          ].map((stat, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + idx * 0.05 }}
              whileHover={{ scale: 1.02, y: -5 }}
              className="gaming-card"
            >
              <Card className="bg-[#0d0d15] border-0 p-4 relative overflow-hidden">
                <div className="absolute inset-0 hex-pattern opacity-20 pointer-events-none" />
                <div className="absolute top-0 left-0 w-full h-[2px]" style={{ background: `linear-gradient(90deg, transparent, ${stat.color}, transparent)` }} />
                <div className="relative">
                  <div className="flex items-center justify-between mb-2">
                    <stat.icon className="w-5 h-5" style={{ color: stat.color }} />
                  </div>
                  <p className="text-xs text-gray-500">{stat.title}</p>
                  <p className="text-xl font-black" style={{ color: stat.color }}>
                    <AnimatedNumber value={stat.value} prefix={stat.prefix || ''} />
                  </p>
                </div>
              </Card>
            </motion.div>
          ))}
        </motion.div>

        {/* Page Filter Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="mb-6"
        >
          <PageFilterTabs />
        </motion.div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Player List */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="lg:col-span-2"
          >
            <Card className="bg-[#0d0d15] border-0 overflow-hidden">
              <div className="absolute inset-0 hex-pattern opacity-10 pointer-events-none" />
              <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#00F5FF] to-transparent" />
              
              {/* Tabs & Search */}
              <div className="p-4 border-b border-white/5 relative">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full sm:w-auto">
                    <TabsList className="grid grid-cols-3 bg-[#1a1a2e]">
                      <TabsTrigger value="all" className="data-[state=active]:bg-[#00F5FF]/20 data-[state=active]:text-[#00F5FF]">
                        <Sword className="w-4 h-4 mr-1" /> All
                      </TabsTrigger>
                      <TabsTrigger value="top" className="data-[state=active]:bg-[#FFD700]/20 data-[state=active]:text-[#FFD700]">
                        <Crown className="w-4 h-4 mr-1" /> Elite
                      </TabsTrigger>
                      <TabsTrigger value="inactive" className="data-[state=active]:bg-[#FF3366]/20 data-[state=active]:text-[#FF3366]">
                        <Skull className="w-4 h-4 mr-1" /> MIA
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                  
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <Input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Hunt warriors..."
                      className="pl-9 bg-[#1a1a2e] border-white/5 text-white"
                      data-testid="players-search-input"
                    />
                  </div>
                </div>
              </div>

              {/* Player List */}
              <ScrollArea className="h-[500px] relative">
                <div className="p-4 space-y-2">
                  {loading ? (
                    <div className="flex justify-center py-12">
                      <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1 }}>
                        <RefreshCw className="w-8 h-8 text-[#00F5FF]" />
                      </motion.div>
                    </div>
                  ) : players.length === 0 ? (
                    <div className="text-center py-12">
                      <Sword className="w-12 h-12 mx-auto text-gray-600 mb-3" />
                      <p className="text-gray-500">No warriors found</p>
                    </div>
                  ) : (
                    players.map((player, idx) => (
                      <motion.div
                        key={player.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.03 }}
                        whileHover={{ scale: 1.01, x: 5 }}
                        onClick={(e) => {
                          if (e.target.closest('[data-actions="true"]')) return;
                          setShowPlayerDetail(player);
                        }}
                        className="gaming-card cursor-pointer"
                      >
                        <Card className="bg-[#1a1a2e] border-0 p-4 hover:border-[#00F5FF]/30 border border-transparent transition-all">
                          <div className="flex items-center gap-4">
                            <GamingAvatar name={player.name} rank={getPlayerRank(player)} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="font-bold text-white truncate">{player.name}</p>
                                {getPlayerRank(player) === 'legendary' && <Crown className="w-4 h-4 text-[#FFD700]" />}
                                {getPlayerRank(player) === 'epic' && <Gem className="w-4 h-4 text-[#9945FF]" />}
                              </div>
                              <p className="text-xs text-gray-500">Level {Math.floor(player.total_deposits / 100)}</p>
                              {player.page_id && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-gray-400">{getPageName(player.page_id)}</span>
                              )}
                            </div>
                            <div className="hidden sm:flex items-center gap-6 text-right">
                              <div>
                                <p className="text-xs text-gray-500">Deposited</p>
                                <p className="font-mono text-[#00FF88]">{formatCurrency(player.total_deposits)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Power</p>
                                <p className={`font-mono font-bold ${player.holding_percentage >= 50 ? 'text-[#00FF88]' : 'text-[#FF3366]'}`}>
                                  {player.holding_percentage.toFixed(0)}%
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1" data-actions="true" onClick={(e) => e.stopPropagation()}>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => { e.stopPropagation(); e.preventDefault(); setShowEditPlayer(player); setPlayerForm({ name: player.name, page_id: player.page_id || '' }); }}
                                className="h-8 w-8 hover:bg-[#00F5FF]/10"
                                data-testid={`edit-player-${player.id}`}
                              >
                                <Edit2 className="w-4 h-4 text-[#00F5FF]" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleDeletePlayer(player.id); }}
                                className="h-8 w-8 hover:bg-[#FF3366]/10"
                                data-testid={`delete-player-${player.id}`}
                              >
                                <Trash2 className="w-4 h-4 text-[#FF3366]" />
                              </Button>
                            </div>
                          </div>
                        </Card>
                      </motion.div>
                    ))
                  )}
                </div>
              </ScrollArea>
              
              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="p-4 border-t border-white/5">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-400">
                      Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalCount)} of {totalCount} warriors
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                        className="border-white/10 hover:bg-white/5 disabled:opacity-30"
                        data-testid="pagination-first"
                      >
                        First
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className="border-white/10 hover:bg-white/5 disabled:opacity-30"
                        data-testid="pagination-prev"
                      >
                        Prev
                      </Button>
                      
                      <div className="flex items-center gap-1">
                        {[...Array(Math.min(5, totalPages))].map((_, idx) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = idx + 1;
                          } else if (currentPage <= 3) {
                            pageNum = idx + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + idx;
                          } else {
                            pageNum = currentPage - 2 + idx;
                          }
                          
                          return (
                            <Button
                              key={pageNum}
                              variant={currentPage === pageNum ? "default" : "outline"}
                              size="sm"
                              onClick={() => setCurrentPage(pageNum)}
                              className={currentPage === pageNum 
                                ? "bg-gradient-to-r from-[#00F5FF] to-[#9945FF] text-black font-bold" 
                                : "border-white/10 hover:bg-white/5"
                              }
                              data-testid={`pagination-page-${pageNum}`}
                            >
                              {pageNum}
                            </Button>
                          );
                        })}
                      </div>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        className="border-white/10 hover:bg-white/5 disabled:opacity-30"
                        data-testid="pagination-next"
                      >
                        Next
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                        className="border-white/10 hover:bg-white/5 disabled:opacity-30"
                        data-testid="pagination-last"
                      >
                        Last
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </Card>
          </motion.div>

          {/* Leaderboards */}
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="space-y-6"
          >
            {/* Hall of Fame */}
            <Card className="bg-[#0d0d15] border-0 overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#FFD700] to-transparent" />
              <div className="p-4">
                <h3 className="font-bold text-white flex items-center gap-2 mb-4">
                  <Trophy className="w-5 h-5 text-[#FFD700]" />
                  HALL OF FAME
                </h3>
                <div className="space-y-2">
                  {getTopDepositors().map((player, idx) => (
                    <motion.div
                      key={player.id}
                      whileHover={{ x: 5 }}
                      className="flex items-center gap-3 p-2 rounded-lg bg-white/5 hover:bg-white/10 cursor-pointer"
                      onClick={() => setShowPlayerDetail(player)}
                    >
                      <span className="text-lg">{['🥇', '🥈', '🥉', '4️⃣', '5️⃣'][idx]}</span>
                      <GamingAvatar name={player.name} rank={getPlayerRank(player)} size="sm" />
                      <span className="flex-1 font-medium text-white truncate">{player.name}</span>
                      <span className="font-mono text-[#FFD700]">{formatCurrency(player.total_deposits)}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </Card>

            {/* Top Power */}
            <Card className="bg-[#0d0d15] border-0 overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#9945FF] to-transparent" />
              <div className="p-4">
                <h3 className="font-bold text-white flex items-center gap-2 mb-4">
                  <Zap className="w-5 h-5 text-[#9945FF]" />
                  POWER RANKINGS
                </h3>
                <div className="space-y-2">
                  {getTopWinners().map((player, idx) => (
                    <motion.div
                      key={player.id}
                      whileHover={{ x: 5 }}
                      className="flex items-center gap-3 p-2 rounded-lg bg-white/5 hover:bg-white/10 cursor-pointer"
                      onClick={() => setShowPlayerDetail(player)}
                    >
                      <span className="w-6 h-6 rounded-full bg-[#9945FF]/20 flex items-center justify-center text-xs font-bold text-[#9945FF]">{idx + 1}</span>
                      <GamingAvatar name={player.name} rank={getPlayerRank(player)} size="sm" />
                      <span className="flex-1 font-medium text-white truncate">{player.name}</span>
                      <span className="font-mono text-[#9945FF]">{formatCurrency(player.holding_amount)}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      </main>

      {/* Add Player Modal */}
      <AnimatePresence>
        {showAddPlayer && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setShowAddPlayer(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md"
            >
              <Card className="bg-[#0d0d15] border-[#00F5FF]/30 p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-3 rounded-xl bg-[#00F5FF]/20">
                    <UserPlus className="w-6 h-6 text-[#00F5FF]" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">RECRUIT WARRIOR</h2>
                    <p className="text-sm text-gray-400">Add new member to the guild</p>
                  </div>
                </div>
                <form onSubmit={handleAddPlayer} className="space-y-4">
                  <div>
                    <Label className="text-gray-300">Warrior Name</Label>
                    <Input
                      value={playerForm.name}
                      onChange={(e) => setPlayerForm({ ...playerForm, name: e.target.value })}
                      placeholder="Enter name..."
                      className="bg-[#1a1a2e] border-white/10 text-white mt-1"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Assign to Page</Label>
                    <select
                      value={playerForm.page_id}
                      onChange={(e) => setPlayerForm({ ...playerForm, page_id: e.target.value })}
                      className="w-full mt-1 h-10 rounded-md bg-[#1a1a2e] border border-white/10 text-white px-3 text-sm"
                      data-testid="add-player-page-select"
                    >
                      <option value="">No Page</option>
                      {branchPages.map(pg => (
                        <option key={pg.id} value={pg.id}>{pg.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex gap-2 pt-4">
                    <Button type="submit" className="flex-1 bg-gradient-to-r from-[#00F5FF] to-[#9945FF] text-black font-bold">
                      <Check className="w-4 h-4 mr-2" /> RECRUIT
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setShowAddPlayer(false)} className="flex-1 border-white/10">
                      Cancel
                    </Button>
                  </div>
                </form>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Player Modal */}
      <AnimatePresence>
        {showEditPlayer && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setShowEditPlayer(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md"
            >
              <Card className="bg-[#0d0d15] border-[#9945FF]/30 p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-3 rounded-xl bg-[#9945FF]/20">
                    <Edit2 className="w-6 h-6 text-[#9945FF]" />
                  </div>
                  <h2 className="text-xl font-bold text-white">EDIT WARRIOR</h2>
                </div>
                <form onSubmit={handleEditPlayer} className="space-y-4">
                  <div>
                    <Label className="text-gray-300">Warrior Name</Label>
                    <Input
                      value={playerForm.name}
                      onChange={(e) => setPlayerForm({ ...playerForm, name: e.target.value })}
                      className="bg-[#1a1a2e] border-white/10 text-white mt-1"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Assign to Page</Label>
                    <select
                      value={playerForm.page_id}
                      onChange={(e) => setPlayerForm({ ...playerForm, page_id: e.target.value })}
                      className="w-full mt-1 h-10 rounded-md bg-[#1a1a2e] border border-white/10 text-white px-3 text-sm"
                      data-testid="edit-player-page-select"
                    >
                      <option value="">No Page</option>
                      {branchPages.map(pg => (
                        <option key={pg.id} value={pg.id}>{pg.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex gap-2 pt-4">
                    <Button type="submit" className="flex-1 bg-[#9945FF]">
                      <Check className="w-4 h-4 mr-2" /> Save
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setShowEditPlayer(null)} className="flex-1 border-white/10">
                      Cancel
                    </Button>
                  </div>
                </form>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Player Detail Modal */}
      <AnimatePresence>
        {showPlayerDetail && (
          <PlayerDetailModal player={showPlayerDetail} onClose={() => setShowPlayerDetail(null)} />
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setDeleteConfirm(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-sm"
            >
              <Card className="bg-[#0d0d15] border border-[#FF3366]/30 p-6 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#FF3366]/20 flex items-center justify-center">
                  <Trash2 className="w-8 h-8 text-[#FF3366]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Banish This Player?</h3>
                <p className="text-gray-400 text-sm mb-6">This action cannot be undone. The player and all associated data will be permanently removed.</p>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1 border-white/10"
                    onClick={() => setDeleteConfirm(null)}
                    data-testid="cancel-delete-player"
                  >
                    Cancel
                  </Button>
                  <Button
                    className="flex-1 bg-[#FF3366] hover:bg-[#FF3366]/80 text-white"
                    onClick={confirmDeletePlayer}
                    data-testid="confirm-delete-player"
                  >
                    Delete
                  </Button>
                </div>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Player Detail Modal
function PlayerDetailModal({ player, onClose }) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player.id]);

  const fetchTransactions = async () => {
    try {
      const response = await axios.get(`/transactions?player_id=${player.id}`);
      setTransactions(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getPlayerRank = () => {
    if (player.total_deposits >= 5000) return { rank: 'legendary', color: '#FFD700', label: 'LEGENDARY' };
    if (player.total_deposits >= 2000) return { rank: 'epic', color: '#9945FF', label: 'EPIC' };
    if (player.total_deposits >= 500) return { rank: 'rare', color: '#00F5FF', label: 'RARE' };
    return { rank: 'common', color: '#888', label: 'WARRIOR' };
  };

  const { rank, color, label } = getPlayerRank();

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, y: 50 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 50 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl max-h-[90vh] overflow-hidden"
      >
        <Card className="bg-[#0d0d15] border-0 overflow-hidden">
          {/* Header */}
          <div className="relative p-6" style={{ background: `linear-gradient(135deg, ${color}20, transparent)` }}>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="absolute top-4 right-4 hover:bg-white/10"
            >
              <X className="w-5 h-5 text-gray-400" />
            </Button>
            
            <div className="flex items-center gap-4">
              <GamingAvatar name={player.name} rank={rank} size="lg" />
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-2xl font-black text-white">{player.name}</h2>
                  {rank === 'legendary' && <Crown className="w-6 h-6 text-[#FFD700]" />}
                </div>
                <Badge style={{ background: `${color}20`, color, borderColor: `${color}50` }} className="border mt-1">
                  {label}
                </Badge>
                <p className="text-sm text-gray-400 mt-1">Level {Math.floor(player.total_deposits / 100)}</p>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-4 gap-3 mt-6">
              {[
                { label: 'Deposited', value: player.total_deposits, color: '#00FF88' },
                { label: 'Withdrawn', value: player.total_withdrawals, color: '#FF3366' },
                { label: 'Loaded', value: player.total_loaded, color: '#00F5FF' },
                { label: 'Power', value: `${player.holding_percentage.toFixed(0)}%`, color: '#FFD700', noFormat: true },
              ].map((stat, idx) => (
                <div key={idx} className="p-3 rounded-xl bg-white/5 text-center">
                  <p className="text-xs text-gray-500">{stat.label}</p>
                  <p className="font-mono font-bold" style={{ color: stat.color }}>
                    {stat.noFormat ? stat.value : formatCurrency(stat.value)}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Transactions */}
          <div className="p-4">
            <h3 className="font-bold text-white mb-3 flex items-center gap-2">
              <Swords className="w-4 h-4 text-[#00F5FF]" /> BATTLE HISTORY
            </h3>
            <ScrollArea className="h-64">
              {loading ? (
                <div className="flex justify-center py-8">
                  <RefreshCw className="w-6 h-6 text-[#00F5FF] animate-spin" />
                </div>
              ) : transactions.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No battles yet</div>
              ) : (
                <div className="space-y-2">
                  {transactions.map((tx, idx) => (
                    <motion.div
                      key={tx.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      className="flex items-center justify-between p-3 rounded-lg bg-white/5"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${tx.transaction_type === 'deposit' ? 'bg-[#00FF88]/10' : 'bg-[#FF3366]/10'}`}>
                          {tx.transaction_type === 'deposit' ? <ArrowUpRight className="w-4 h-4 text-[#00FF88]" /> : <ArrowDownRight className="w-4 h-4 text-[#FF3366]" />}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-white">{tx.game_name || 'Battle'}</p>
                          <p className="text-xs text-gray-500">{formatRelativeTime(tx.created_at)}</p>
                        </div>
                      </div>
                      <p className={`font-mono font-bold ${tx.transaction_type === 'deposit' ? 'text-[#00FF88]' : 'text-[#FF3366]'}`}>
                        {tx.transaction_type === 'deposit' ? '+' : '-'}{formatCurrency(tx.deposit_amount || tx.amount || 0)}
                      </p>
                    </motion.div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
        </Card>
      </motion.div>
    </motion.div>
  );
}
